import { useEffect, useState } from 'react';
import { AddContactRequest, EditContactRequest, ContactsRequest, SingleContact } from 'vopay';
import axios from 'axios';
import vopayApi from '@/services/api.config';

const { api } = vopayApi;

export default function useContacts() {
  const source = axios.CancelToken.source();
  const [pending, setPending] = useState(false);
  const [contact, setContact] = useState<SingleContact | null>(null);
  const [contacts, setContacts] = useState<SingleContact[]>([]);

  // TODO: this cancel is a no-op — vopay's http.ts puts `cancelToken` in the query params, not the axios
  // config. So leaving /contacts before the list loads (browser Back; the loader covers the sidebar) keeps the
  // request running, and ContactsPage's blocking loader covers the next page until it finishes. Fix in http.ts.
  useEffect(() => {
    return () => source.cancel();
  }, []);

  async function createContact(params: AddContactRequest) {
    try {
      setPending(true);
      const res = await api.addContact(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return {
        contactID: res.ContactID,
      };
    } finally {
      setPending(false);
    }
  }

  async function fetchContact(contactID: string) {
    try {
      setPending(true);
      const res = await api.fetchContacts(
        {
          ContactID: contactID,
        },
        source,
      );

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const contact = res.Contacts.find((a) => a.ContactID === contactID) || null;
      setContact(contact);

      return contact;
    } finally {
      setPending(false);
    }
  }

  async function fetchContacts(params: ContactsRequest) {
    try {
      setPending(true);
      const res = await api.fetchContacts(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setContacts(res.Contacts);

      return res;
    } finally {
      setPending(false);
    }
  }

  async function editContact(params: EditContactRequest) {
    try {
      setPending(true);
      const res = await api.editContact(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } finally {
      setPending(false);
    }
  }

  return {
    pending,
    createContact,
    contact,
    fetchContact,
    contacts,
    fetchContacts,
    editContact,
  };
}
